'use client';

import { useBodyScrollLock } from '@common/hooks';
import { cn } from '@core/utils';
import type { ReactNode } from 'react';
import { useRef } from 'react';

import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePublishElementHeight } from '@/hooks/usePublishElementHeight';

interface BottomSheetProps {
  /** 상단 다크 토스트 영역 (optional) */
  toast?: ReactNode;
  /** 하단 흰색 영역 내 콘텐츠 */
  children: ReactNode;
  /** 뒤쪽 딤 배경 표시 (외부 상태로 제어) */
  backdrop?: boolean;
  /** backdrop 클릭 시 콜백 */
  onBackdropClick?: () => void;
  className?: string;
  /** 콘텐츠 영역 커스텀 className */
  contentClassName?: string;
}

export const BottomSheet = ({
  toast,
  children,
  backdrop = false,
  onBackdropClick,
  className,
  contentClassName,
}: BottomSheetProps) => {
  const isMobileTablet = useMediaQuery(BREAKPOINTS.MOBILE_TABLET, false);

  if (!isMobileTablet) return null;

  return (
    <BottomSheetContent
      toast={toast}
      backdrop={backdrop}
      onBackdropClick={onBackdropClick}
      className={className}
      contentClassName={contentClassName}
    >
      {children}
    </BottomSheetContent>
  );
};

const BottomSheetContent = ({
  toast,
  children,
  backdrop = false,
  onBackdropClick,
  className,
  contentClassName,
}: BottomSheetProps) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(backdrop);

  usePublishElementHeight(sheetRef, '--bottom-nav-portal-height');

  return (
    <>
      {/* 백드롭 */}
      {backdrop && (
        <div
          role="presentation"
          onClick={onBackdropClick}
          className="fixed inset-0 z-30 bg-Gray-black/50 transition-opacity duration-300"
        />
      )}

      <div
        ref={sheetRef}
        className={cn('fixed inset-x-0 bottom-0 z-40', className)}
      >
        <div
          className={cn(
            'overflow-hidden shadow-[0_0.25rem_0.25rem_0_rgba(0,0,0,0.06),0_1.25rem_1.25rem_0_rgba(0,0,0,0.04),0.375rem_-0.375rem_1.25rem_0_rgba(0,0,0,0.04)]',
            toast ? 'bg-Gray-black' : 'bg-Gray-white',
          )}
        >
          {toast && (
            <div className="flex items-center justify-center px-16 py-8">
              {toast}
            </div>
          )}

          <div className={cn('bg-Gray-white px-20 py-12', contentClassName)}>
            {children}
          </div>
        </div>
      </div>
    </>
  );
};
